import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/i18n/I18nContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { AuthProvider } from "@/context/AuthContext";
import { ImageSlotsProvider } from "@/context/ImageSlotsContext";
import { AppShell } from "@/components/AppShell";

import Home from "@/pages/Home";

const NexisQuiz = lazy(() => import("@/pages/NexisQuiz"));
const BsCallPro = lazy(() => import("@/pages/BsCallPro"));
const NexisBuilder = lazy(() => import("@/pages/NexisBuilder"));
const Solutions = lazy(() => import("@/pages/Solutions"));
const SolutionFamily = lazy(() => import("@/pages/SolutionFamily"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Sourcing = lazy(() => import("@/pages/Sourcing"));
const Experts = lazy(() => import("@/pages/Experts"));
const Network = lazy(() => import("@/pages/Network"));
const HNPillar = lazy(() => import("@/pages/HNPillar"));
const ModularProduct = lazy(() => import("@/pages/ModularProduct"));
const Energy = lazy(() => import("@/pages/Energy"));
const EnergyRouter = lazy(() => import("@/pages/EnergyRouter"));
const CaboVerde = lazy(() => import("@/pages/CaboVerde"));
const Tecnologia = lazy(() => import("@/pages/Tecnologia"));
const Pilots = lazy(() => import("@/pages/Pilots"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const ProjectsMap = lazy(() => import("@/pages/ProjectsMap"));
const HxEcosystems = lazy(() => import("@/pages/BrandPages").then((module) => ({ default: module.HxEcosystems })));
const Nexis = lazy(() => import("@/pages/BrandPages").then((module) => ({ default: module.Nexis })));
const EcoCollection = lazy(() => import("@/pages/BrandPages").then((module) => ({ default: module.EcoCollection })));
const IntegratedSolutions = lazy(() => import("@/pages/IntegratedSolutions"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Partners = lazy(() => import("@/pages/Partners"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const Faq = lazy(() => import("@/pages/Faq"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Admin = lazy(() => import("@/pages/admin/Admin"));

function RouteLoader() {
  return <div className="flex min-h-[42vh] items-center justify-center bg-canvas" role="status" aria-label="Loading"><span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-bsgo" /></div>;
}

function App() {
  return (
    <I18nProvider>
      <SettingsProvider>
        <AuthProvider>
          <ImageSlotsProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route element={<AppShell />}>
                <Route path="/" element={<Home />} />
                {/* BSGOTECH NETWORK — primary platform routes */}
                <Route path="/nexis-quiz" element={<NexisQuiz />} />
                <Route path="/bs-call-pro" element={<BsCallPro />} />
                <Route path="/nexis-builder" element={<NexisBuilder />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/product/:id" element={<ProductDetail />} />
                <Route path="/solutions/:slug" element={<SolutionFamily />} />
                <Route path="/sourcing" element={<Sourcing />} />
                <Route path="/experts" element={<Experts />} />
                <Route path="/network" element={<Network />} />
                {/* HX ECOSYSTEMS — five HX technology pillars */}
                <Route path="/solar" element={<HNPillar slug="solar" />} />
                <Route path="/water" element={<HNPillar slug="water" />} />
                <Route path="/modular" element={<HNPillar slug="modular" />} />
                <Route path="/e-mobility" element={<HNPillar slug="e-mobility" />} />
                <Route path="/digital" element={<HNPillar slug="digital" />} />
                {/* Retained depth routes */}
                <Route path="/modular/:slug" element={<ModularProduct />} />
                <Route path="/energy" element={<Energy />} />
                <Route path="/energy/:slug" element={<EnergyRouter />} />
                <Route path="/integrated-solutions" element={<IntegratedSolutions />} />
                <Route path="/pilots" element={<Pilots />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/projects-map" element={<ProjectsMap />} />
                <Route path="/hx-ecosystems" element={<HxEcosystems />} />
                <Route path="/nexis" element={<Nexis />} />
                <Route path="/eco-collection" element={<EcoCollection />} />
                <Route path="/nu-bai" element={<Navigate to="/projects" replace />} />
                <Route path="/obogo" element={<Navigate to="/projects" replace />} />
                <Route path="/obo-pay" element={<Navigate to="/projects" replace />} />
                <Route path="/cabo-verde" element={<CaboVerde />} />
                <Route path="/tecnologia" element={<Tecnologia />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/legal" element={<Legal type="legal" />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/terms" element={<Legal type="terms" />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin" element={<Admin />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          </ImageSlotsProvider>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </SettingsProvider>
    </I18nProvider>
  );
}

export default App;
